'use client';

import { BlurSwap } from '../../collection-showcase/scenes/playback-controls.jsx';
import { ShowcaseIcon } from '../../collection-showcase/scenes/scene-primitives.jsx';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import {
  ShowcaseCopyContext,
  translateShowcaseNode,
} from '../../collection-showcase/translation';
import { Zi, Xi, ta, $i, FinanceWorkflow } from './wholesale-order';
import { useBusinessPlayback, BusinessIcon } from './business-primitives';
import { xe, C } from './business-facts';
export function FinanceSequence({
  start: e,
  single: t,
  running: n,
  onDone: r,
  onPick: i,
  onInteract: a,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let o = t ? 1 : Zi.length - e,
    { t: s, reduced: c } = useBusinessPlayback(Xi * o, {
      running: n,
      onDone: t ? ta : r,
    }),
    l = Math.min(o - 1, Math.floor(s / Xi)),
    u = e + l,
    d = c ? Xi : s - l * Xi;
  return (
    <div className={'flex flex-col gap-3 p-2 md:gap-4 md:p-4'}>
      <div
        role={'group'}
        aria-label={translateShowcaseNode(showcaseCopy, 'Finance workflows')}
        className={'grid grid-cols-2 gap-1.5 lg:grid-cols-4'}
      >
        {translateShowcaseNode(
          showcaseCopy,
          Zi.map((e, t) => {
            let n = t === u;
            return (
              <button
                key={e.name}
                type={'button'}
                aria-pressed={n}
                onClick={() => i(t)}
                className={`press relative flex min-h-12 flex-col overflow-hidden rounded-[10px] px-3 py-2.5 text-left transition-[background-color] duration-150 ${n ? 'bg-[var(--state-wash)]' : 'hover:bg-[var(--hover-wash)]'}`}
              >
                <span
                  className={
                    'text-ink flex items-center gap-2 text-[13px] leading-[18px] font-medium'
                  }
                >
                  <ShowcaseIcon
                    name={e.icon}
                    className={`size-4 shrink-0 ${n ? 'text-ink' : 'text-ink-3'}`}
                  />
                  {translateShowcaseNode(showcaseCopy, e.name)}
                </span>
                <span
                  className={
                    'text-ink-3 mt-1 hidden pl-6 text-[12px] leading-4 sm:block'
                  }
                >
                  {translateShowcaseNode(showcaseCopy, $i(e.name))}
                </span>
                {translateShowcaseNode(
                  showcaseCopy,
                  n && !c ? (
                    <span
                      aria-hidden={!0}
                      className={
                        'absolute inset-x-3 bottom-1 h-[2px] overflow-hidden rounded-full bg-[var(--tint-2)]'
                      }
                    >
                      <span
                        className={
                          'block h-full origin-left bg-[var(--primary)]'
                        }
                        style={{
                          transform: `scaleX(${d / Xi})`,
                        }}
                      />
                    </span>
                  ) : null
                )}
              </button>
            );
          })
        )}
      </div>
      <div className={'relative lg:grid'}>
        {translateShowcaseNode(
          showcaseCopy,
          Zi.map((e, t) => (
            <div
              key={e.name}
              className={`panel min-w-0 ${t === u ? '' : 'max-lg:absolute max-lg:inset-x-0 max-lg:top-0'}`}
              data-active={t === u}
              inert={t !== u}
            >
              <FinanceWorkflow index={t} t={t === u ? d : Xi} onInteract={a} />
            </div>
          ))
        )}
      </div>
      <div
        className={
          'flex flex-col gap-2 px-1 md:flex-row md:items-start md:justify-between md:gap-6'
        }
      >
        <div className={'flex min-w-0 flex-col gap-1.5'}>
          <p className={'label'}>
            {translateShowcaseNode(
              showcaseCopy,
              'Business Finance \xB7 8 sections'
            )}
          </p>
          <p className={'text-ink-3 text-[12px] leading-[1.5] sm:hidden'}>
            {translateShowcaseNode(showcaseCopy, xe.sections.join(' \xB7 '))}
          </p>
          <ul className={'flex flex-wrap gap-1 max-sm:hidden'}>
            {translateShowcaseNode(
              showcaseCopy,
              xe.sections.map((e) => (
                <li
                  key={e}
                  className={
                    'text-ink-3 flex h-6 items-center rounded-full bg-[var(--tint)] px-2.5 text-[12px]'
                  }
                >
                  {translateShowcaseNode(showcaseCopy, e)}
                </li>
              ))
            )}
          </ul>
        </div>
        <p
          className={
            'text-ink-3 max-w-[300px] shrink-0 text-[12.5px] leading-[1.5] md:text-right'
          }
        >
          {translateShowcaseNode(showcaseCopy, xe.method)}
          {translateShowcaseNode(showcaseCopy, ' ')}
          <span className={'text-ink-4'}>
            {translateShowcaseNode(showcaseCopy, 'Figures are illustrative.')}
          </span>
        </p>
      </div>
    </div>
  );
}
export function FinanceAnimation({ running: e, onInteract: t, onDone: n }) {
  let [r, i] = React.useState(null);
  return (
    <FinanceSequence
      key={r ? `${r.index}-${r.visit}` : 'auto'}
      start={r?.index ?? 0}
      single={r !== null}
      running={e}
      onDone={n}
      onPick={(e) => {
        (t(),
          i((t) => ({
            index: e,
            visit: (t?.visit ?? 0) + 1,
          })));
      }}
      onInteract={t}
    />
  );
}
export function ScheduleCard({ nextRun: e, runs: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'pop enter w-full max-w-[420px] rounded-2xl p-1.5'}>
      <div
        className={'flex items-start gap-3 rounded-[10px] px-2.5 pt-2.5 pb-2'}
      >
        <span
          className={
            'text-ink grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--muted)]'
          }
        >
          <ShowcaseIcon name={'clock'} className={'size-4'} />
        </span>
        <span className={'min-w-0 flex-1'}>
          <span className={'text-ink block text-[14px] leading-5 font-medium'}>
            {translateShowcaseNode(showcaseCopy, 'Monday cash check')}
          </span>
          <span className={'text-ink-3 block text-[12px] leading-4'}>
            {translateShowcaseNode(
              showcaseCopy,
              'Weekly \xB7 Monday 8:00 AM ET'
            )}
          </span>
        </span>
        <span
          className={
            'text-ink-3 flex h-5 shrink-0 items-center gap-1 rounded-full bg-[var(--tint)] px-2 text-[11.5px]'
          }
        >
          <span className={'size-1.5 rounded-full bg-emerald-500'} />
          {translateShowcaseNode(showcaseCopy, 'Active')}
        </span>
      </div>
      <div
        className={
          'mx-2.5 flex items-center justify-between gap-3 border-t border-[color-mix(in_srgb,var(--border)_60%,transparent)] py-2 text-[12.5px]'
        }
      >
        <span className={'text-ink-3'}>
          {translateShowcaseNode(showcaseCopy, 'Next run')}
        </span>
        <span className={'num text-ink font-medium'}>
          <BlurSwap value={e} />
        </span>
      </div>
      <div
        className={
          'mx-2.5 flex items-center gap-1.5 border-t border-[color-mix(in_srgb,var(--border)_60%,transparent)] py-2'
        }
      >
        <span className={'text-ink-3 mr-1 text-[12px]'}>
          {translateShowcaseNode(showcaseCopy, 'Runs')}
        </span>
        {translateShowcaseNode(
          showcaseCopy,
          t.map((e) => (
            <span
              key={e.date}
              className={`num flex h-6 items-center gap-1 rounded-full px-2 text-[11.5px] transition-colors duration-200 ${e.state === 'scheduled' ? 'text-ink-3 shadow-[inset_0_0_0_1px_var(--border)]' : 'text-ink bg-[var(--tint)]'}`}
            >
              {translateShowcaseNode(
                showcaseCopy,
                e.state === 'done' ? (
                  <ShowcaseIcon
                    key={'done'}
                    name={'check'}
                    draw={'now'}
                    className={'size-3'}
                    strokeWidth={2.2}
                  />
                ) : e.state === 'running' ? (
                  <span className={'size-1.5 rounded-full bg-sky-500'} />
                ) : null
              )}
              {translateShowcaseNode(showcaseCopy, e.date)}
            </span>
          ))
        )}
        <span
          className={
            'text-ink-3 ml-auto flex h-8 items-center gap-1 rounded-md px-1.5 text-[12px]'
          }
        >
          {translateShowcaseNode(showcaseCopy, 'View in Schedule')}
          <BusinessIcon name={'external'} className={'size-3'} />
        </span>
      </div>
    </div>
  );
}
export function DateDivider({ label: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'enter flex items-center gap-3 py-1'} role={'separator'}>
      <span
        className={
          'h-px flex-1 bg-[color-mix(in_srgb,var(--border)_70%,transparent)]'
        }
      />
      <span className={'num text-ink-3 flex items-center gap-1.5 text-[12px]'}>
        <ShowcaseIcon name={'clock'} className={'size-3.5'} />
        {translateShowcaseNode(showcaseCopy, e)}
      </span>
      <span
        className={
          'h-px flex-1 bg-[color-mix(in_srgb,var(--border)_70%,transparent)]'
        }
      />
    </div>
  );
}
export function ScheduleResult({ done: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <span
      className={
        'enter text-ink flex h-9 w-fit max-w-full items-center gap-2 rounded-full px-3.5 text-[13px] shadow-[inset_0_0_0_1px_var(--border)]'
      }
    >
      <BusinessIcon name={'message'} className={'text-ink-3 size-4 shrink-0'} />
      <span className={'min-w-0 truncate'}>
        {translateShowcaseNode(showcaseCopy, 'Monday cash check \xB7 Oct 12')}
      </span>
      <span className={'text-ink-3 shrink-0 text-[12px]'}>
        <BlurSwap value={e ? 'Done' : 'Running'} />
      </span>
    </span>
  );
}
export const sa = 'Mercer & Vale';
export const ca =
  ' every Monday morning, update the cash forecast and tell me if the low week moves.';
export const la = 24;
export const ua = C.low;
export const da = (e) => `$${e.toLocaleString('en-US')}`;
export const fa = 114800;
export const z = {
  type: 400,
  send: 2858,
  worked: 4300,
  reply: 4500,
  card: 4900,
  jump: 6900,
  running: 7200,
  report: 8600,
  chip: 9e3,
};
export const pa = 10600;
export function GrowingMessage({ children: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'bz-grow'}>
      <div>
        <div className={'bz-recv pt-3'}>
          {translateShowcaseNode(showcaseCopy, e)}
        </div>
      </div>
    </div>
  );
}
export function SchedulePrompt() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'flex justify-end'}>
      <p
        className={
          'text-ink max-w-[92%] rounded-[18px] bg-[var(--secondary)] px-4 py-2 text-[15px] leading-6 shadow-[var(--shadow-minimal)] sm:max-w-[84%]'
        }
      >
        <span className={'ref ref-amber'}>
          {translateShowcaseNode(showcaseCopy, sa)}
        </span>
        {translateShowcaseNode(showcaseCopy, ca)}
      </p>
    </div>
  );
}
